import { atom } from "jotai";
import {
  askHelenaConversationId,
  askHelenaMessages,
  resetAskHelenaSession,
} from "./askHelenaAtoms";

/**
 * Ask Helena conversation the user opened from history. Drives the messages
 * fetch; once it resolves, `askHelenaConversationId` holds the same id.
 * `null` is the empty state — the greeting, the queue summary and the chips.
 */
export const selectedConversationIdAtom = atom<string | null>(null);

/**
 * Whether a conversation is open at all — the greeting/queue-summary empty state
 * on false, the chat on true.
 *
 * Derived from the ids rather than from `messages`, because switching threads
 * empties the messages until the fetch settles: anything keyed off the message
 * count flickers off and back on across that gap. Both ids move inside a single
 * writer, so this never dips between them.
 *
 * Messages are still consulted for the one case the ids miss — a first question
 * is on screen before the `conversation` event names the thread.
 */
export const hasOpenConversationAtom = atom(
  (get) =>
    Boolean(get(askHelenaConversationId) ?? get(selectedConversationIdAtom)) ||
    get(askHelenaMessages).length > 0,
);

/** Text sitting in the composer, kept across conversation switches. */
export const chatDraftAtom = atom("");

/**
 * The voice session the draft's dictated text came from, sent with it as
 * `voiceSessionId` so the recording is linked to the message. Cleared with the
 * draft. A second dictation into the same draft replaces it — one id per
 * question is all `/ask` takes (DESIGN §10 B3).
 */
export const chatDraftVoiceSessionIdAtom = atom<string | null>(null);

/** Sidebar rail visibility, driven by the panel toggle in its header. */
export const isChatSidebarOpenAtom = atom(true);

/** Disclosure state of the sidebar's "Recent" group. */
export const isRecentChatsOpenAtom = atom(true);

/**
 * Sidebar row currently being renamed, if any. At most one at a time — a second
 * editor would have two pending titles for the same list.
 */
export const renamingConversationIdAtom = atom<string | null>(null);

/** Row whose delete confirmation is open. Null closes the modal. */
export const pendingDeleteConversationIdAtom = atom<string | null>(null);

/** While open, the search panel takes over the chat panel — thread included. */
export const isChatSearchOpenAtom = atom(false);

/** Text typed into the search panel; dropped whenever the panel closes. */
export const chatSearchQueryAtom = atom("");

export const closeChatSearch = atom(null, (_get, set) => {
  set(isChatSearchOpenAtom, false);
  set(chatSearchQueryAtom, "");
});

/**
 * Opens a real conversation from the sidebar or the search results. Clearing
 * the live session matters: a live thread always wins in ChatPanel and would
 * otherwise hide the selection.
 */
export const openAskHelenaConversation = atom(
  null,
  (get, set, conversationId: string) => {
    set(closeChatSearch);
    // Already on screen — re-fetching it would throw away a live turn for a
    // thread the user is already reading.
    if (get(askHelenaConversationId) === conversationId) return;

    set(resetAskHelenaSession);
    set(selectedConversationIdAtom, conversationId);
  },
);

/** Back to the empty state, with nothing carried over from the last thread. */
export const startNewChat = atom(null, (_get, set) => {
  set(resetAskHelenaSession);
  set(selectedConversationIdAtom, null);
  set(closeChatSearch);
});
